import {
  passwordSchema,
  PASSWORD_MIN_LENGTH,
  USERNAME_MAX_LENGTH,
  usernameSchema,
} from '@pixelworld/shared';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Field, FormError } from '../../components/Field';
import { SkyPage } from '../../components/SkyPage';
import { da } from '../../i18n/da';
import { register } from './auth-api';
import { useAuth } from './auth-context';
import styles from './AuthPage.module.css';

export function RegisterPage() {
  const { state, signedIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Only the bootstrap link for the first admin carries a code. Players sign up without one.
  const [inviteCode] = useState(() => searchParams.get('invite') ?? undefined);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (state.status === 'ready' && state.user) {
    return <Navigate to="/" replace />;
  }

  const valid =
    usernameSchema.safeParse(username).success && passwordSchema.safeParse(password).success;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== repeat) {
      setError(da.auth.passwordsDiffer);
      return;
    }
    setBusy(true);
    setError(null);
    const result = await register({ inviteCode, username, password });
    setBusy(false);
    if (!result.ok) {
      setError(da.authErrors[result.error]);
      return;
    }
    signedIn(result.data.user);
    void navigate('/', { replace: true });
  };

  return (
    <SkyPage>
      <Card className={styles.card} aria-labelledby="register-title">
        <h2 id="register-title" className={styles.title}>
          {da.auth.registerTitle}
        </h2>
        <p className={styles.intro}>{da.auth.registerIntro}</p>
        <form className={styles.form} onSubmit={(event) => void handleSubmit(event)}>
          <Field
            label={da.auth.newUsernameLabel}
            hint={da.auth.usernameHint}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder={da.auth.usernamePlaceholder}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={USERNAME_MAX_LENGTH}
            required
          />
          <Field
            label={da.auth.newPasswordLabel}
            hint={da.auth.passwordHint(PASSWORD_MIN_LENGTH)}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            required
          />
          <Field
            label={da.auth.repeatPasswordLabel}
            type="password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            autoComplete="new-password"
            required
          />
          <FormError message={error} />
          <Button type="submit" disabled={busy || !valid || !repeat}>
            {da.auth.registerButton}
          </Button>
        </form>
        <p className={styles.footer}>
          {da.auth.haveAccount} <Link to="/login">{da.auth.goToLogin}</Link>
        </p>
      </Card>
    </SkyPage>
  );
}
